import { FC, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { MostSlowestModelsQuery } from '__generated__/graphql';
import { max } from 'd3';

import {
  Accessor,
  BarGroups,
  Bars,
  ButtonGroup,
  Card,
  CategoryAxis,
  categoryScale,
  ChartProps,
  IconButton,
  InteractionItem,
  LinearAxis,
  linearScale,
  Plot,
  RyeconInfoOutline,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAdminEnvironment } from '@/hooks/admin/environments';
import { useSlowestModels } from '@/hooks/discovery/slowestModels';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { ArrElement } from '@/typings/arrayElement';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { generateDurationFormatter } from '@/util/formatDuration';
import { ChartDateDropdown, DateRange, DEFAULT_DATE_RANGE } from './ChartDateDropdown';
import { ChartWrapper } from './ChartWrapper';

export interface SlowestModelsColumnChartProps {}

type SlowestModel = ArrElement<
  MostSlowestModelsQuery['performance']['longestExecutedModels']
>;

export const SlowestModelsColumnChart: FC<SlowestModelsColumnChartProps> = () => {
  const [selectedTab, setSelectedTab] = useState<'Max' | 'Job'>('Max');

  const { accountId, projectId, environmentId } = useDbtCloudProject();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);

  const projectScope = useUrlProjectParams();
  const getJobLink = useLinkToDbtCloud('jobDetails');

  const [slowestModels] = useSlowestModels({
    environmentId,
    startDate: dateRange?.startDate || '',
    endDate: dateRange?.endDate || '',
    limit: 5,
    // pause: false,
  });

  const getResourceDetailsPageUrl = useLinkToExplorer('resourceDetails');
  const navigate = useNavigate();

  if (!accountId || !projectId) {
    throw new Error(
      'This component expects to be in a URL with accountId and projectId parameters!',
    );
  }
  const environment = useAdminEnvironment(
    environmentId,
    accountId,
    undefined,
    true,
    selectedTab === 'Job',
  );
  const jobNamesById = (environment?.data?.jobs || []).reduce<Record<number, string>>(
    (acc, job) => {
      acc[job.id] = job.name;
      return acc;
    },
    {},
  );

  const rawData = slowestModels.data?.performance?.longestExecutedModels;
  const { data, formatDuration, maxTimeSecs, numJobs } = useMemo(() => {
    const maxTimeSecs = max(rawData || [], (x) => x.maxExecutionTime) || 0;

    const formatDuration = generateDurationFormatter('s', maxTimeSecs);

    const numJobs = rawData?.[0]?.byJob?.length;

    return {
      data: rawData as SlowestModel[] | null,
      numJobs,
      formatDuration,
      maxTimeSecs,
    };
  }, [rawData]);

  useEffect(() => {
    if (numJobs === 1) {
      setSelectedTab('Job');
    }
  }, [numJobs]);

  const chartProps: ChartProps | null = useMemo(() => {
    const chartData = data ?? [];

    const xAccessor: Accessor<SlowestModel, string> = (x) =>
      parseUniqueId(x.uniqueId || '').remainder;

    const yAccessor = (x: SlowestModel) =>
      formatDuration.toMaxUnits('s', x?.maxExecutionTime ?? 0);

    const yScale = linearScale({
      min: 0,
      max: yAccessor({ maxExecutionTime: maxTimeSecs } as SlowestModel),
    });
    const xScale = categoryScale({ labels: chartData.map(xAccessor) });
    const chartProps: Partial<ChartProps> = {
      margin: { left: 50, right: 5, top: 20, bottom: 40 },
      yScale,
      xScale,
      xAccessor,
      yAccessor,
      tooltip: undefined,
      onItemClick(item: InteractionItem<SlowestModel>) {
        const nodeIsDeleted = item?.item.isDeleted;
        if (nodeIsDeleted) return;

        const url = getResourceDetailsPageUrl({
          ...projectScope,
          dbtUniqueId: item?.item?.uniqueId || '',
          tab: 'performance',
        });
        navigate(url);
      },
    };

    if (selectedTab === 'Max') {
      chartProps.tooltip = ({ interactionItem }: TooltipCallbackArgs<SlowestModel>) => {
        if (!interactionItem?.item) {
          return null;
        }
        const nodeIsDeleted = interactionItem.item.isDeleted;
        return (
          <div className="text-xs">
            {formatDuration.humanize('s', interactionItem?.item.maxExecutionTime ?? 0)}
            {nodeIsDeleted && (
              <div className="text-xs">
                <i>This is a deleted model.</i>
              </div>
            )}
          </div>
        );
      };
    } else {
      chartProps.tooltip = ({ interactionItem }: TooltipCallbackArgs<SlowestModel>) => {
        if (!interactionItem?.item) {
          return null;
        }
        const nodeIsDeleted = interactionItem.item.isDeleted;
        return (
          <div className="flex flex-col space-y-2">
            {interactionItem.item?.byJob?.map((job, index) => {
              const projectLink = getJobLink?.({
                ...projectScope,
                jobId: job.jobId || 0,
                projectId: projectId || 0,
              });

              return (
                <div key={index}>
                  <div className="text-sm">
                    {projectLink ? (
                      <Link to={projectLink} className="text-fgBrand">
                        Job: {jobNamesById[job.jobId || 0] || job.jobId}
                      </Link>
                    ) : (
                      <>Job: {jobNamesById[job.jobId || 0] || job.jobId}</>
                    )}
                  </div>
                  <div className="text-xs">
                    Execution Time:{' '}
                    {formatDuration.humanize('s', job.maxExecutionTime ?? 0)}
                  </div>
                  {nodeIsDeleted && (
                    <div className="text-xs">
                      <i>This is a deleted model.</i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      };
    }

    return chartProps as ChartProps;
  }, [
    data,
    formatDuration,
    maxTimeSecs,
    selectedTab,
    getResourceDetailsPageUrl,
    projectId,
    navigate,
    getJobLink,
    projectScope,
    jobNamesById,
  ]);

  return (
    <Card>
      <div>
        <div className="mb-5 flex flex-wrap justify-end space-y-2">
          <div
            className={`ml-[${chartProps?.margin.left}px] mr-2 flex-1 justify-start whitespace-nowrap text-xl`}
          >
            Longest model executions
            <IconButton
              onClick={() => {
                window.open(
                  'https://docs.getdbt.com/docs/build/incremental-models-overview',
                  '_blank',
                );
              }}
              ryecon={RyeconInfoOutline}
              className="ml-2"
            />
          </div>
          {!!numJobs && numJobs > 1 && (
            <ButtonGroup testId="slowest-models-chart-tab-switcher">
              <ButtonGroup.Button
                text="Max"
                onClick={() => setSelectedTab('Max')}
                isDisabled={selectedTab === 'Max'}
              />
              <ButtonGroup.Button
                text="Job"
                onClick={() => setSelectedTab('Job')}
                isDisabled={selectedTab === 'Job'}
              />
            </ButtonGroup>
          )}
          <ChartDateDropdown onChange={setDateRange} />
        </div>
        <ChartWrapper
          numDataPoints={data?.length}
          noDataMessage="No models were executed in this time period"
          {...chartProps}
          height={350}
          isLoading={slowestModels.fetching}
          error={slowestModels.error?.message}
        >
          <CategoryAxis
            axis="x"
            label="Model: longest execution time"
            enableTruncation
          />
          <LinearAxis
            axis="y"
            label={`Execution time (${formatDuration.unitsAbbrev})`}
          />
          <Plot
            data={data ?? []}
            itemComponent={selectedTab === 'Max' ? Bars : BarGroups}
            extraItemProps={{
              className:
                'fill-fgVizIndigo transition-all hover:fill-fgVizIndigoHover hover:cursor-pointer',
              ...(selectedTab === 'Job'
                ? {
                    groupValuesAccessor: (item: SlowestModel) =>
                      (item.byJob || []).map((x) =>
                        formatDuration.toMaxUnits('s', x.maxExecutionTime ?? 0),
                      ),
                  }
                : {}),
            }}
          />
        </ChartWrapper>
      </div>
    </Card>
  );
};
